import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { ProfileDropdown } from '../components/ProfileDropdown';
import { useAuth } from '../contexts/AuthContext';
import { loadLocations, getUserRole } from '../lib/api';
import { supabase, type Location } from '../lib/supabase';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export function SetStation() {
  return (
    <ProtectedRoute>
      <SetStationContent />
    </ProtectedRoute>
  );
}

function SetStationContent() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [nameRequired, setNameRequired] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError('');
      setNameRequired(false);

      let locs: Location[] = [];
      const locationsResult = await loadLocations();
      if (locationsResult.success && locationsResult.data) {
        locs = locationsResult.data;
        setLocations(locs);
      } else {
        setError(locationsResult.error || 'Failed to load stations');
      }

      let nextRoleId: number | null = null;
      let nextJudgestation: number | null = null;

      if (user?.email) {
        const roleResponse = await getUserRole(user.email);
        if (roleResponse.success && roleResponse.data) {
          nextRoleId = roleResponse.data.roleid;
          nextJudgestation = roleResponse.data.judgestation;
        }

        const { data: official } = await supabase
          .from('raceofficial')
          .select('displayname, roleid, judgestation')
          .eq('email', user.email)
          .eq('isactive', true)
          .maybeSingle();

        if (official) {
          const existingName = official.displayname && String(official.displayname).trim();
          if (existingName) {
            setDisplayName(existingName);
            setNameRequired(false);
          } else {
            // displayname empty/NULL — leave blank and require entry
            setDisplayName('');
            setNameRequired(true);
          }
          if (official.roleid != null) nextRoleId = official.roleid;
          if (official.judgestation != null) nextJudgestation = official.judgestation;
        } else {
          setDisplayName('');
          setNameRequired(true);
        }
      }

      // One-time station prefill only — do not re-apply after user changes selection
      if (locs.length > 0) {
        const saved = sessionStorage.getItem('currentLocation');
        if (saved && locs.some((loc) => loc.locationid.toString() === saved)) {
          setSelectedLocation(saved);
        } else if (nextRoleId === 4 && nextJudgestation != null) {
          const judgeLoc = locs.find((loc) => loc.locationid === nextJudgestation);
          setSelectedLocation(
            judgeLoc ? judgeLoc.locationid.toString() : locs[0].locationid.toString()
          );
        } else {
          setSelectedLocation(locs[0].locationid.toString());
        }
      }

      setIsLoading(false);
    };

    load();
  }, [user?.email]);

  const selected = locations.find(
    (loc) => loc.locationid.toString() === selectedLocation
  );
  const penaltyTime = selected?.penaltytime ?? null;

  const handleStart = async () => {
    setError('');

    const trimmedName = displayName.trim();
    if (!trimmedName) {
      setError('Please enter your name');
      return;
    }
    if (!selectedLocation) {
      setError('Please select a station');
      return;
    }
    if (!user?.email) {
      setError('You must be signed in to continue');
      return;
    }

    setIsSaving(true);
    try {
      const { error: updateError } = await supabase
        .from('raceofficial')
        .update({ displayname: trimmedName })
        .eq('email', user.email)
        .eq('isactive', true);

      if (updateError) {
        console.error('Failed to save displayname:', updateError);
        setError(updateError.message || 'Failed to save your name');
        setIsSaving(false);
        return;
      }

      sessionStorage.setItem('currentLocation', selectedLocation);
      sessionStorage.setItem('savedLocation', selectedLocation);
      sessionStorage.setItem('stationSetupComplete', '1');
      navigate('/head-judge-dashboard');
    } catch (err) {
      console.error('Error starting judging:', err);
      setError(err instanceof Error ? err.message : 'Failed to save and continue');
      setIsSaving(false);
    }
  };

  return (
    <div className="pen-screen">
      <header className="pen-topbar">
        <Link to="/set-station" className="block">
          <img
            src="/HyroxLogo.png"
            alt="Hyrox Logo"
            className="h-9 w-auto object-contain"
          />
        </Link>
        <div className="ml-auto">
          <ProfileDropdown variant="pen" />
        </div>
      </header>

      <div className="px-4 py-5 flex flex-col gap-3 max-w-lg mx-auto">
        <div>
          <div className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-sw-muted mb-1">
            Set your station
          </div>
          <h1 className="text-[32px] font-black uppercase tracking-[0.02em] text-white leading-none">
            Head Judge
            <br />
          </h1>
        </div>

        {error && <div className="pen-error">{error}</div>}

        <div>
          <label htmlFor="display-name" className="pen-label">
            Your name
          </label>
          {nameRequired && !displayName.trim() && !isLoading && (
            <div className="pen-note mb-2">
              Please enter your name before starting. This is how you will appear on
              logged penalties.
            </div>
          )}
          <input
            id="display-name"
            type="text"
            className={`pen-input ${
              nameRequired && !displayName.trim() && !isLoading
                ? '!border-sw-yellow ring-1 ring-sw-yellow'
                : ''
            }`}
            placeholder="Enter your name"
            value={isLoading ? '' : displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={isLoading || isSaving}
            autoComplete="name"
            required
            autoFocus={nameRequired}
          />
        </div>

        <div>
          <label className="pen-label">Station</label>
          <Select
            value={selectedLocation}
            onValueChange={setSelectedLocation}
            disabled={isLoading || isSaving || locations.length === 0}
          >
            <SelectTrigger className="w-full min-h-11 h-auto bg-black border border-sw-yellow rounded-none px-3.5 py-3 text-[15px] font-extrabold uppercase tracking-[0.03em] text-white focus:ring-1 focus:ring-sw-yellow [&>svg]:text-sw-yellow [&>svg]:opacity-100">
              <SelectValue placeholder="Select station" />
            </SelectTrigger>
            <SelectContent className="bg-sw-panel border-sw-border text-white font-brutal">
              {locations.map((location) => (
                <SelectItem
                  key={location.locationid}
                  value={location.locationid.toString()}
                  className="text-sm uppercase focus:bg-sw-yellow focus:text-black"
                >
                  {location.locationname || `Location ${location.locationid}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="pen-note mt-1">
          Your active station for penalties, DNFs, and violations
        </p>
        {selected && (
          <div className="bg-sw-yellow text-black rounded-sm border-2 border-sw-yellow px-4 py-3.5 flex items-center gap-4">
            <div className="font-mono font-light text-[48px] leading-none tabular-nums">
              {penaltyTime ?? '—'}
            </div>
            <div className="min-w-0">
              <div className="text-[13px] font-black uppercase tracking-[0.05em]">
                Sec · Standard
              </div>
              <div className="text-xs font-semibold mt-1 leading-snug opacity-80">
                Base penalty time for{' '}
                <span className="font-extrabold uppercase">
                  {selected.locationname || 'this station'}
                </span>
                . Violations may apply additional time.
              </div>
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={handleStart}
          disabled={isLoading || isSaving || !selectedLocation || !displayName.trim()}
          className="pen-btn-primary w-full mt-9 !text-sm !min-h-12"
        >
          {isSaving ? 'Saving…' : 'Start judging'}
        </button>
      </div>
    </div>
  );
}
